import type { UseControllerProps } from 'react-hook-form'
import type { DataOption, GroupBase, OptionsOrGroups } from './types'

import React from 'react'
import { CircleHelpIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import {
	MultiSelect,
	MultiSelectCheckboxItem,
	MultiSelectCheckboxSelectAll,
	MultiSelectContent,
	MultiSelectEmpty,
	MultiSelectGroup,
	MultiSelectItem,
	MultiSelectList,
	MultiSelectSearch,
	MultiSelectSeparator,
	MultiSelectTrigger,
	MultiSelectValue,
} from '@/shared/components/ui/multi-select'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import {
	Virtualized,
	VirtualizedVirtualizer,
} from '@/shared/components/ui/virtualizer'
import { cn } from '@/shared/lib/utils'

import { gatFlatOptions, isGrouped } from './utils'

export default function RHFMultiSelect<OptionType extends DataOption>({
	name,
	label,
	hint,
	shouldUnregister,
	defaultValue,
	description,
	options,
	disabled,
	placeholder,
	className,
	enableVirtualization = true,
	isLoading,
	isOptionDisabled,
}: UseControllerProps & {
	options: OptionsOrGroups<OptionType, GroupBase<OptionType>>
} & {
	placeholder?: string
	className?: string
	disabled?: boolean
	label: string
	hint?: string
	description?: string
	enableVirtualization?: boolean
	isLoading?: boolean
	isOptionDisabled?: (option: OptionType) => boolean
}) {
	const { t } = useTranslation()

	const { control } = useFormContext()
	const flatOptions = React.useMemo(() => gatFlatOptions(options), [options])

	return (
		<FormField
			name={name}
			defaultValue={defaultValue}
			shouldUnregister={shouldUnregister}
			control={control}
			render={({ field, fieldState: { error } }) => (
				<FormItem>
					<FormLabel>
						{label}
						{hint && (
							<Tooltip>
								<TooltipTrigger asChild>
									<CircleHelpIcon className='size-3 text-muted-foreground' />
								</TooltipTrigger>
								<TooltipContent side='top'>{hint}</TooltipContent>
							</Tooltip>
						)}
					</FormLabel>
					<MultiSelect
						isLoading={isLoading}
						disabled={disabled}
						options={flatOptions}
						onSearch={enableVirtualization ? () => {} : undefined}
						onValueChange={field.onChange}
						value={field.value}
					>
						<FormControl data-name={name}>
							<MultiSelectTrigger
								ref={field.ref}
								aria-invalid={!!error}
								className={cn('overflow-auto', className)}
							>
								<MultiSelectValue
									maxDisplay={2}
									placeholder={placeholder ?? t('general_select')}
								/>
							</MultiSelectTrigger>
						</FormControl>
						{enableVirtualization ? (
							<MultiSelectVirtualContent
								isOptionDisabled={isOptionDisabled}
								options={flatOptions}
							/>
						) : (
							<MultiSelectContent>
								<MultiSelectSearch placeholder={t('general_search')} />
								<MultiSelectList className='max-h-[calc(var(--radix-popper-available-height)-36px)]'>
									{isGrouped(options)
										? (options as GroupBase<OptionType>[]).map(
												(optionGroup) => (
													<MultiSelectGroup
														key={optionGroup.label}
														heading={optionGroup.label}
													>
														{optionGroup.options?.map((option) => (
															<MultiSelectItem
																data-value={option.value}
																key={option.value}
																value={option.value}
																keywords={[option.label]}
																disabled={isOptionDisabled?.(option)}
															>
																{option.label}
															</MultiSelectItem>
														))}
													</MultiSelectGroup>
												)
											)
										: (options as OptionType[]).map((option) => (
												<MultiSelectItem
													data-value={option.value}
													value={option.value}
													key={option.value}
													keywords={[option.label]}
												>
													{option.label}
												</MultiSelectItem>
											))}
								</MultiSelectList>
							</MultiSelectContent>
						)}
					</MultiSelect>
					{!!description && <FormDescription>{description}</FormDescription>}
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}

function MultiSelectVirtualContent<T extends DataOption>({
	options,
	isOptionDisabled,
}: {
	options: T[]
	isOptionDisabled?: (option: T) => boolean
}) {
	const { t } = useTranslation()

	const [searchText, setSearchText] = React.useState('')
	const filteredOptions = options.filter((option) =>
		option.label.toLowerCase().includes(searchText.toLowerCase() ?? [])
	)

	return (
		<MultiSelectContent>
			<MultiSelectSearch
				onValueChange={setSearchText}
				value={searchText}
				placeholder='Input to search'
			/>
			<Virtualized asChild>
				<MultiSelectList className='max-h-[calc(var(--radix-popper-available-height)-36px)] scroll-pt-12'>
					<MultiSelectEmpty />
					{!!filteredOptions.length && (
						<MultiSelectGroup className='sticky top-0 z-10 bg-popover'>
							<MultiSelectCheckboxSelectAll
								values={filteredOptions
									.filter((o) =>
										isOptionDisabled ? !isOptionDisabled(o) : true
									)
									.map(({ value }) => value)}
							>
								{t('general_select_all')}
							</MultiSelectCheckboxSelectAll>
						</MultiSelectGroup>
					)}
					<MultiSelectSeparator className='sticky top-10' />
					<MultiSelectGroup>
						<VirtualizedVirtualizer>
							{filteredOptions.map((option) => (
								<MultiSelectCheckboxItem
									key={option.value}
									keywords={[option.label]}
									value={option.value}
									disabled={isOptionDisabled?.(option)}
								>
									{option.label}
								</MultiSelectCheckboxItem>
							))}
						</VirtualizedVirtualizer>
					</MultiSelectGroup>
				</MultiSelectList>
			</Virtualized>
		</MultiSelectContent>
	)
}
